<template>
  <v-card style="position: relative;">
    <v-card-text style="min-height: 396px;">
      <div>
        <div class="subheading font-weight-medium baseline">
          Include QA/QC sample data
          <v-menu offset-y open-on-hover :open-delay="400">
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="clickable ml-2" color="primary">
                info_outline
              </v-icon>
            </template>
            <v-card>
              <v-card-text>
                Include data from QA/QC replicate samples.
              </v-card-text>
            </v-card>
          </v-menu>
        </div>

        <v-radio-group :disabled="disabled" class="mt-1 mb-4" hide-details v-model="reportOptions.includeQaQc">
          <v-layout>
            <v-flex>
              <v-radio hide-details color="primary" label="Yes" :value="true"></v-radio>
            </v-flex>
            <v-flex>
              <v-radio hide-details color="primary" label="No" :value="false"></v-radio>
            </v-flex>
          </v-layout>
        </v-radio-group>
      </div>

      <div>
        <div class="subheading font-weight-medium baseline">
          Include Internal EnviroData Analyte name
          <v-menu offset-y open-on-hover :open-delay="400">
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="clickable ml-2" color="primary">
                info_outline
              </v-icon>
            </template>
            <v-card>
              <v-card-text>
                Internal EnviroData names are unique analyte names that help us manage analytes and potential synonyms.
                Display names are not unique and the internal EnviroData name will provide an indication of the
                difference between repeating display names.
              </v-card-text>
            </v-card>
          </v-menu>
        </div>
        <v-radio-group :disabled="disabled" class="mt-1 mb-4" hide-details v-model="reportOptions.includeEnvirodataAnalyteName">
          <v-layout>
            <v-flex>
              <v-radio color="primary" label="Yes" :value="true" />
            </v-flex>
            <v-flex>
              <v-radio color="primary" label="No" :value="false" />
            </v-flex>
          </v-layout>
        </v-radio-group>
      </div>

      <div>
        <div class="subheading font-weight-medium baseline">
          Report format
          <v-menu offset-y open-on-hover :open-delay="400">
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="clickable ml-2" color="primary">
                info_outline
              </v-icon>
            </template>
            <v-card>
              <v-card-text>
                <div class="mb-2">
                  <strong>Standard</strong> reports show analytes as rows &mdash;
                  this format is similar to most EnviroData reports.
                </div>
                <div class="mb-2">
                  <strong>Wide</strong> reports show analytes as columns.
                </div>
                <div>
                  <strong>Long</strong> reports are easily parsed by programming languages such as R.
                </div>
              </v-card-text>
            </v-card>
          </v-menu>
        </div>
        <v-radio-group :disabled="disabled" class="mt-1 mb-4" hide-details v-model="reportOptions.reportFormat">
          <v-layout>
            <v-flex v-for="(reportType, index) in reportTypes" :key="`reportType-${index}`">
              <v-radio color="primary" :value="reportType.value">
                <template v-slot:label>
                  <div>
                    <div class="font-weight-">{{ reportType.name }}</div>
                    <div class="caption">{{ reportType.format }}</div>
                  </div>
                </template>
              </v-radio>
            </v-flex>
          </v-layout>
        </v-radio-group>
      </div>

      <transition name="fade">
        <div v-if="reportTypesMapping[reportOptions.reportFormat].canGroupWorksheet">
          <div>
            <div class="subheading font-weight-medium baseline">
              Group worksheet data by
              <v-menu offset-y open-on-hover :open-delay="400">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" class="clickable ml-2" color="primary">
                    info_outline
                  </v-icon>
                </template>
                <v-card>
                  <v-card-text>
                    As an example, if <strong>Stations and Media Types</strong> is selected,
                    then a station will have a spreadsheet for each of its media types.
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            <v-radio-group ref="organizeWorksheetGroup" :disabled="disabled" class="mt-1 mb-4" hide-details v-model="reportOptions.organizeWorksheetBy">
              <transition-group class="layout" name="fade" tag="div">
                <v-flex v-for="(reportGrouping, index) in reportTypesMapping[reportOptions.reportFormat].reportGroupings" :key="`reportGrouping-${index}`">
                  <v-radio :key="reportGrouping.name" color="primary" :label="reportGrouping.name" :value="reportGrouping.value">
                  </v-radio>
                </v-flex>
              </transition-group>
            </v-radio-group>
          </div>

          <transition name="fade">
            <div v-if="reportTypesMapping[reportOptions.reportFormat].canIncludeStatSummary">
              <div class="subheading font-weight-medium baseline">
                Include statistical data summary
                <v-menu offset-y open-on-hover :open-delay="400">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" class="clickable ml-2" color="primary">
                      info_outline
                    </v-icon>
                  </template>
                  <v-card>
                    <v-card-text>
                      Adds a table at the bottom of every spreadsheet that includes summary statistics for each analyte.
                      <br />
                      Statistics include: min, max, mean, median, standard deviation, standard error, sample size,
                      number of non-detects, and percentage of non-detects.
                      <br /><br />
                      Mean, median, standard deviation and standard error are only calculated if the percentage of
                      non-detects is less than 50%.
                    </v-card-text>
                  </v-card>
                </v-menu>
              </div>
              <v-radio-group :disabled="disabled" class="mt-1 mb-4" hide-details v-model="reportOptions.includeSummaryTab">
                <v-layout>
                  <v-flex>
                    <v-radio color="primary" label="Yes" :value="true">
                    </v-radio>
                  </v-flex>
                  <v-flex>
                    <v-radio color="primary" label="No" :value="false"></v-radio>
                  </v-flex>
                </v-layout>
              </v-radio-group>
            </div>
          </transition>
        </div>
      </transition>
    </v-card-text>
  </v-card>
</template>

<script>
  import {
    reportTypes,
    reportGroupings,
    reportTypesMapping,
    defaultReportType,
    defaultReportGrouping,
  } from '@/helpers/multiStationQuery'

  export default {
    props: {
      selection: Object,
      disabled: Boolean,
    },
    data() {
      return {
        reportTypes,
        reportTypesMapping,
        reportGroupings,
        initialReportOptions: {
          includeQaQc: false,
          reportFormat: defaultReportType,
          organizeWorksheetBy: defaultReportGrouping,
          includeSummaryTab: false,
          includeEnvirodataAnalyteName: false,
        },
        reportOptions: {

        },
      }
    },
    watch: {
      reportOptions: {
        immediate: true,
        handler(value) {
          this.$emit('update:reportOptions', value)
        },
      },
      'reportOptions.reportFormat'(value) {
        let reportType = reportTypesMapping[value]

        let hasValue = (
          reportType.reportGroupings.some(reportGrouping =>
            reportGrouping.value.every(rg => (
                Array.isArray(this.reportOptions.organizeWorksheetBy) &&
                this.reportOptions.organizeWorksheetBy.includes(rg)) &&
              reportGrouping.value.length === this.reportOptions.organizeWorksheetBy.length
            ))
        )

        if (!hasValue) {
          if (reportType.reportGroupings.length > 0) {
            this.reportOptions.organizeWorksheetBy = reportType.reportGroupings[0].value
          } else {
            this.reportOptions.organizeWorksheetBy = null
          }
        }
      },
    },
    methods: {
      loadReportOptions(reportOptions) {
        if (reportOptions) this.reportOptions = reportOptions
        else this.reset()
      },
      reset() {
        this.reportOptions = JSON.parse(JSON.stringify(this.initialReportOptions))
      },
    },
    created() {
      this.reset()
    },
  }
</script>

<style scoped>
  .baseline {
    align-items: baseline;
    display: flex;
  }
</style>